import {forwardRef, useMemo} from "react";
import {BoxProps} from "@mantine/core";
import {
    IProtocol,
    IProtocolStep,
    IProtocolStepKind,
    IProtocolStepStatus,
    IGroupStep,
    TProtocolStepId,
} from "../../types/protocol"
import {Box} from "@mantine/core";
import classes from "./StepsView.module.css"
import {StepIcon} from "../../common/StepIcon";
import cx from "clsx";
import {TSubjectId} from "@/common/types";
import {StepStatusIcon} from "@/protocols/studies/steps/StepStatusIcon.tsx";
import {DeepPartial} from "@/utils/types/DeepPartial";


interface StepsNavigatorProps extends BoxProps {
    protocol: IProtocol
    activeStep: TProtocolStepId
    subjectId?: TSubjectId
    onStepSelection: (id: TProtocolStepId) => void
    onStepChange: (stepId: TProtocolStepId, updates: DeepPartial<IProtocolStep>) => void
}

export const StepsView = forwardRef<HTMLDivElement, StepsNavigatorProps>(({
                                                                              protocol,
                                                                              activeStep,
                                                                              onStepSelection,
                                                                              subjectId,
                                                                              onStepChange,
                                                                              ...others
                                                                          }: StepsNavigatorProps, ref) => {

    // Get all step IDs that are members of any group
    const groupedStepIds = useMemo(() => {
        const ids = new Set<TProtocolStepId>();
        protocol.steps.forEach(step => {
            if (step.kind === IProtocolStepKind.GROUP) {
                const group = step as IGroupStep;
                group.memberStepIds?.forEach(id =>
                        ids.add(id)
                );
            }
        });
        return ids;
    }, [protocol.steps]);

    // Filter steps for display: only show ungrouped steps and groups
    const displaySteps = useMemo(() => {
        return protocol.steps.filter(step => !groupedStepIds.has(step.id));
    }, [protocol.steps, groupedStepIds]);


    const onStepSelected = (id: TProtocolStepId) => () => {
        onStepSelection(id)
    }
    const renderStepStatus = (status: IProtocolStepStatus, step: IProtocolStep, index: number) => {
        const handleStatusChange = (newStatus: IProtocolStepStatus) => {
            // Only allow status changes for regular steps, not groups
            if (step.kind !== IProtocolStepKind.GROUP) {
                onStepChange(step.id, {status: newStatus});
            }
        };

        let icon
        switch (status) {
            case IProtocolStepStatus.COMPLETED:
                icon = <StepStatusIcon status={IProtocolStepStatus.COMPLETED} selected={true}
                                       onChange={() => handleStatusChange(IProtocolStepStatus.COMPLETED)}
                                       data-testid={`${status}-${index}`}/>
                break;
            case IProtocolStepStatus.SKIPPED:
                icon = <StepStatusIcon status={IProtocolStepStatus.SKIPPED} selected={true}
                                       onChange={() => handleStatusChange(IProtocolStepStatus.SKIPPED)}
                                       data-testid={`${status}-${index}`}/>
                break;
            case IProtocolStepStatus.INCOMPLETE:
                icon = <StepStatusIcon status={IProtocolStepStatus.INCOMPLETE} selected={true}
                                       onChange={() => handleStatusChange(IProtocolStepStatus.INCOMPLETE)}
                                       data-testid={`${status}-${index}`}/>
                break;
            default:
                // this is here for exhaustive check purposes
                assertNever(status)
                break;
        }
        return (<div className={`${classes.status}`} onClick={onStepSelected(step.id)}>{icon}</div>)
    }

    const assertNever = (value: never): never => {
        throw new Error(`Unexpected value: ${value}`);
    }

    const renderProtocolSteps = (steps: Array<IProtocolStep>) => {
        return steps.map((step, index) => {
                const isActive = step.id === activeStep

                return (
                    <div key={`step-${step.id}`} className={cx(classes.row, {active: isActive})}>
                        <Box pr={"0.25em"} className={classes.indicatorContainer}>
                            {isActive ? <div data-testid={`indicator-${step.id}`} className={`${classes.indicator}`}/> : ""}
                        </Box>
                        <div key={`step-${index}`} className={cx(classes.rowInner)}>
                            <div className={classes.stepIcon}>
                                <StepIcon kind={step.kind} size={"1.25rem"}/>
                            </div>
                            <div className={cx(classes.name)}
                                 onClick={onStepSelected(step.id)}>{step.name}</div>
                            {renderStepStatus(step.status, step, index)}
                        </div>
                    </div>
                )
            }
        )
    }

    return (
        <Box ref={ref} {...others} className={classes.stepsContent}>
            {renderProtocolSteps(displaySteps)}
        </Box>
    )
})